import { cn, Input, Modal } from '@crm/ui';
import { ChevronRight, Clock, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type NavItem, useNavSections } from './navigation';

const RECENTS_KEY = 'crm_recent_features';
const MAX_RECENTS = 8;

function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    // Armazenamento bloqueado (aba anonima, politica do navegador): sem
    // historico a busca continua funcionando na ordem do menu.
    return [];
  }
}

function rememberVisit(to: string): string[] {
  const next = [to, ...readRecents().filter((item) => item !== to)].slice(0, MAX_RECENTS);

  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // Sem historico gravado, a proxima abertura volta a ordem do menu.
  }

  return next;
}

/** Tira acento e caixa: "Servicos" acha "serviços" e vice-versa. */
const normalize = (value: string) => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Busca de funcionalidades da barra do topo. O campo abre a modal ao receber
 * o foco; dentro dela a lista se navega com as setas e abre no Enter.
 *
 * Sem busca, as telas vem das usadas por ultimo - o caminho de quem repete o
 * mesmo trabalho todo dia fica curto.
 */
export function FeatureSearch() {
  const sections = useNavSections();
  const navigate = useNavigate();

  const [isOpen, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [cursor, setCursor] = useState(0);
  const [recents, setRecents] = useState<string[]>(() => readRecents());
  const listRef = useRef<HTMLUListElement>(null);

  const all = useMemo(
    () =>
      sections.flatMap((section) =>
        section.items.map((item) => ({ ...item, section: section.title })),
      ),
    [sections],
  );

  const results = useMemo(() => {
    const search = normalize(term.trim());

    if (!search) {
      // Recentes primeiro, na ordem em que foram usadas; o resto segue o menu.
      const byRoute = new Map(all.map((item) => [item.to, item]));
      const used = recents.map((to) => byRoute.get(to)).filter(Boolean) as typeof all;

      return [...used, ...all.filter((item) => !recents.includes(item.to))];
    }

    return all.filter(
      (item) => normalize(item.label).includes(search) || normalize(item.section).includes(search),
    );
  }, [all, recents, term]);

  const open = () => {
    setTerm('');
    setCursor(0);
    setRecents(readRecents());
    setOpen(true);
  };

  const go = (item: NavItem) => {
    setRecents(rememberVisit(item.to));
    setOpen(false);
    navigate(item.to);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((current) => {
        const next =
          event.key === 'ArrowDown'
            ? Math.min(current + 1, results.length - 1)
            : Math.max(current - 1, 0);

        listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
        return next;
      });
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const chosen = results[cursor];
      if (chosen) go(chosen);
    }
  };

  return (
    <>
      {/* Campo so como porta de entrada: quem digita, digita na modal. */}
      <button
        type="button"
        onClick={open}
        onFocus={open}
        className="hidden h-12 w-56 items-center gap-2 rounded-md border border-border bg-muted/50 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted sm:flex lg:w-72"
      >
        <Search className="h-5 w-5 shrink-0" />
        <span className="truncate">Buscar funcionalidade</span>
      </button>

      <button
        type="button"
        aria-label="Buscar funcionalidade"
        onClick={open}
        // Mesmo tom e tamanho dos outros icones da barra do topo.
        className="flex h-11 w-11 items-center justify-center rounded-md bg-transparent text-foreground transition-colors hover:bg-accent sm:hidden"
      >
        <Search className="h-5 w-5" />
      </button>

      {isOpen ? (
        <Modal open onClose={() => setOpen(false)} title="Ir para" size="full">
          <div className="mx-auto w-full max-w-2xl space-y-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                className="h-14 pl-12 text-base"
                placeholder="Buscar funcionalidade"
                value={term}
                onChange={(event) => {
                  // Cada busca recomeca do primeiro resultado.
                  setTerm(event.target.value);
                  setCursor(0);
                }}
                onKeyDown={onKeyDown}
              />
            </div>

            {results.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-10 text-center text-base text-muted-foreground">
                Nenhuma funcionalidade com esse nome.
              </p>
            ) : (
              <ul
                ref={listRef}
                className="divide-y divide-border overflow-hidden rounded-lg border border-border"
              >
                {results.map((item, index) => (
                  <li key={item.to}>
                    <button
                      type="button"
                      onMouseEnter={() => setCursor(index)}
                      onClick={() => go(item)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors',
                        index === cursor ? 'bg-primary/10' : 'hover:bg-muted/60',
                      )}
                    >
                      {/* Icone em chip da cor primaria: a lista e de atalhos,
                          nao de itens desativados. */}
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <item.icon className="h-5 w-5" />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-base font-medium text-foreground">
                          {item.label}
                        </span>
                        <span className="block truncate text-sm text-muted-foreground">
                          {item.section}
                        </span>
                      </span>

                      {!term && recents.includes(item.to) ? (
                        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                      ) : null}

                      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <p className="text-sm text-muted-foreground">
              Use as setas para navegar e Enter para abrir.
            </p>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
